import React, { useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { DatePickerInput } from 'react-native-paper-dates';
import { pt, registerTranslation } from 'react-native-paper-dates';
import {
  Button,
  IconButton,
  List,
  Modal,
  Portal,
  Searchbar,
  Surface,
  Text,
  TextInput,
  Snackbar,
  DataTable,
  useTheme,
  Checkbox,
} from 'react-native-paper';

interface SavedFilter {
  id: string;
  name: string;
  filters: FilterState;
}

interface FilterState {
  filial: string;
  numCarregamento: string;
  dataEmissaoInicial: string;
  dataEmissaoFinal: string;
  dataVencimentoInicial: string;
  dataVencimentoFinal: string;
  dataProrrogacaoInicial: string;
  dataProrrogacaoFinal: string;
  dataDescontoInicial: string;
  dataDescontoFinal: string;
}

interface Boleto {
  codfilial: string;
  codcli: number;
  nome_cli?: string;
  duplic: string;
  valor: number;
  dtemissao: string;
  dtvenc: string;
  codcob: string;
}

// Registra a tradução em português
registerTranslation('pt', pt);

export function ListaBoletos() {
  const theme = useTheme();
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBoletos, setSelectedBoletos] = useState<Set<string>>(new Set());
  const [selectedClients, setSelectedClients] = useState<Set<number>>(new Set());
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [expandedClient, setExpandedClient] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    filial: '',
    numCarregamento: '',
    dataEmissaoInicial: '',
    dataEmissaoFinal: '',
    dataVencimentoInicial: '',
    dataVencimentoFinal: '',
    dataProrrogacaoInicial: '',
    dataProrrogacaoFinal: '',
    dataDescontoInicial: '',
    dataDescontoFinal: '',
  });

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [filterName, setFilterName] = useState('');
  const [snackbar, setSnackbar] = useState({
    visible: false,
    mensagem: '',
    tipo: 'info' as 'info' | 'success' | 'error'
  });

  // Dados mockados para visualização inicial
  const mockBoletos: Boleto[] = [
    {
      codfilial: '001',
      codcli: 1234,
      nome_cli: 'Empresa A Ltda',
      duplic: 'BOL001',
      valor: 1500.50,
      dtemissao: '2025-05-23',
      dtvenc: '2025-06-23',
      codcob: 'BOL',
    },
    {
      codfilial: '001',
      codcli: 1234,
      nome_cli: 'Empresa A Ltda',
      duplic: 'BOL002',
      valor: 2000.00,
      dtemissao: '2025-05-23',
      dtvenc: '2025-06-15',
      codcob: 'BOL',
    },
    {
      codfilial: '002',
      codcli: 5678,
      nome_cli: 'Empresa B Ltda',
      duplic: 'BOL003',
      valor: 2750.75,
      dtemissao: '2025-05-23',
      dtvenc: '2025-06-15',
      codcob: 'BOL',
    },
  ];

  // Agrupa boletos por cliente
  const boletosAgrupados = mockBoletos.reduce((acc, boleto) => {
    const codcli = Number(boleto.codcli);
    if (!acc[codcli]) {
      acc[codcli] = {
        cliente: { codcli, nome: boleto.nome_cli || 'Cliente sem nome' },
        boletos: [],
      };
    }
    acc[codcli].boletos.push(boleto);
    return acc;
  }, {} as Record<number, { cliente: { codcli: number; nome: string }; boletos: Boleto[] }>);

  // Estados
  const [enviando, setEnviando] = useState(false);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSendSelectedBoletos = async () => {
    if (selectedBoletos.size === 0) {
      setSnackbar({
        visible: true,
        mensagem: 'Selecione pelo menos um boleto para enviar.',
        tipo: 'error'
      });
      return;
    }

    try {
      setEnviando(true);
      const boletosParaEnviar = Array.from(selectedBoletos);

      // Feedback inicial
      setSnackbar({
        visible: true,
        mensagem: 'Iniciando envio dos boletos...',
        tipo: 'info'
      });

      // Simulação do envio
      for (const duplic of boletosParaEnviar) {
        const boleto = mockBoletos.find(b => b.duplic === duplic);
        if (!boleto) continue;

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Feedback de sucesso
      setSnackbar({
        visible: true,
        mensagem: `${boletosParaEnviar.length} ${boletosParaEnviar.length === 1 ? 'boleto enviado' : 'boletos enviados'} com sucesso!`,
        tipo: 'success'
      });

      setSelectedBoletos(new Set());
      setSelectedClients(new Set());
    } catch (error) {
      console.error('Erro:', error);
      setSnackbar({
        visible: true,
        mensagem: 'Falha ao enviar boletos. Tente novamente.',
        tipo: 'error'
      });
    } finally {
      setEnviando(false);
    }
  };

  const toggleSelectAllBoletos = () => {
    if (selectedBoletos.size > 0) {
      setSelectedBoletos(new Set());
      setSelectedClients(new Set());
    } else {
      const allBoletos = new Set<string>();
      const allClients = new Set<number>();
      Object.values(boletosAgrupados).forEach(({ cliente, boletos }) => {
        allClients.add(cliente.codcli);
        boletos.forEach(boleto => allBoletos.add(boleto.duplic));
      });
      setSelectedBoletos(allBoletos);
      setSelectedClients(allClients);
    }
  };

  const toggleSelectClient = (codcli: number, boletos: Boleto[]) => {
    const newSelectedBoletos = new Set(selectedBoletos);
    const newSelectedClients = new Set(selectedClients);

    if (selectedClients.has(codcli)) {
      newSelectedClients.delete(codcli);
      boletos.forEach(boleto => newSelectedBoletos.delete(boleto.duplic));
    } else {
      newSelectedClients.add(codcli);
      boletos.forEach(boleto => newSelectedBoletos.add(boleto.duplic));
    }

    setSelectedBoletos(newSelectedBoletos);
    setSelectedClients(newSelectedClients);
  };

  const toggleSelectBoleto = (duplic: string, codcli: number) => {
    const newSelectedBoletos = new Set(selectedBoletos);
    const newSelectedClients = new Set(selectedClients);

    if (selectedBoletos.has(duplic)) {
      newSelectedBoletos.delete(duplic);
      // Verificar se ainda há boletos selecionados para este cliente
      const clienteBoletos = boletosAgrupados[codcli]?.boletos || [];
      const temBoletosSelec = clienteBoletos.some(b => newSelectedBoletos.has(b.duplic));
      if (!temBoletosSelec) {
        newSelectedClients.delete(codcli);
      }
    } else {
      newSelectedBoletos.add(duplic);
      newSelectedClients.add(codcli);
    }

    setSelectedBoletos(newSelectedBoletos);
    setSelectedClients(newSelectedClients);
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const aplicarFiltros = () => {
    setFilterModalVisible(false);
    // Implementar lógica de filtros
  };

  return (
    <View style={styles.container}>
      <Surface style={styles.header} elevation={2}>
        <View style={styles.headerTop}>
          <View style={styles.headerLeft}>
            <Text variant="headlineMedium">Envio de Boletos</Text>
            <Button
              mode="outlined"
              icon={selectedBoletos.size > 0 ? 'checkbox-marked' : 'checkbox-blank-outline'}
              onPress={toggleSelectAllBoletos}
              style={styles.selectAllButton}
            >
              {selectedBoletos.size > 0 ? 'Desmarcar Todos' : 'Selecionar Todos'}
            </Button>
          </View>
          <Button
            mode="contained"
            icon="email-multiple"
            onPress={handleSendSelectedBoletos}
            loading={enviando}
            disabled={selectedBoletos.size === 0 || enviando}
          >
            {enviando ? 'Enviando...' : 'Enviar Selecionados'}
          </Button>
        </View>
        <View style={styles.searchContainer}>
          <Searchbar
            placeholder="Buscar por cliente ou duplicata..."
            onChangeText={handleSearch}
            value={searchQuery}
            style={styles.searchBar}
          />
          <IconButton
            icon="filter"
            size={24}
            onPress={() => setFilterModalVisible(true)}
          />
        </View>
      </Surface>

      <ScrollView style={styles.content}>
        {Object.entries(boletosAgrupados).map(([codcliStr, { cliente, boletos }]) => (
          // Convertendo codcli para número
          <List.Accordion
            key={codcliStr}
            title={
              <View style={styles.accordionHeader}>
                <View style={styles.clientInfo}>
                  <Text variant="titleMedium">{cliente.nome}</Text>
                  <View style={styles.clientStats}>
                    <Text variant="bodySmall">
                      {`${boletos.length} boleto(s) - Total: ${boletos
                        .reduce((sum, b) => sum + b.valor, 0)
                        .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                    </Text>
                    {selectedBoletos.size > 0 && (
                      <Text variant="bodySmall" style={styles.selectedCount}>
                        {`${boletos.filter(b => selectedBoletos.has(b.duplic)).length} selecionado(s)`}
                      </Text>
                    )}
                  </View>
                </View>
                <Button
                  mode="outlined"
                  icon={selectedClients.has(cliente.codcli) ? 'checkbox-marked' : 'checkbox-blank-outline'}
                  onPress={(e) => {
                    e.stopPropagation();
                    toggleSelectClient(cliente.codcli, boletos);
                  }}
                  style={styles.sendClientButton}
                >
                  {selectedClients.has(cliente.codcli) ? 'Desmarcar' : 'Selecionar'}
                </Button>
              </View>
            }
            expanded={expandedClient === cliente.codcli}
            onPress={() => setExpandedClient(expandedClient === cliente.codcli ? null : cliente.codcli)}
            left={props => <List.Icon {...props} icon="account" />}
          >
            <DataTable>
              <DataTable.Header>
                <DataTable.Title style={styles.duplicataHeader}>Duplicata</DataTable.Title>
                <DataTable.Title numeric style={styles.valueHeader}>Valor</DataTable.Title>
                <DataTable.Title style={styles.dateHeader}>Vencimento</DataTable.Title>
                <DataTable.Title style={styles.actionsHeader}>Ações</DataTable.Title>
              </DataTable.Header>

              {boletos.map((boleto, index) => (
                <DataTable.Row key={index}>
                  <DataTable.Cell>
                    <View style={styles.boletoCell}>
                      <Checkbox
                        status={selectedBoletos.has(boleto.duplic) ? 'checked' : 'unchecked'}
                        onPress={() => toggleSelectBoleto(boleto.duplic, cliente.codcli)}
                      />
                      <Text>{boleto.duplic}</Text>
                    </View>
                  </DataTable.Cell>
                  <DataTable.Cell numeric style={styles.valueCell}>
                    {boleto.valor.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </DataTable.Cell>
                  <DataTable.Cell style={styles.dateCell}>{boleto.dtvenc}</DataTable.Cell>
                  <DataTable.Cell>
                    <Button
                      mode="text"
                      icon="email"
                      onPress={() => toggleSelectBoleto(boleto.duplic, cliente.codcli)}
                    >
                      {selectedBoletos.has(boleto.duplic) ? 'Desmarcar' : 'Selecionar'}
                    </Button>
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable>
          </List.Accordion>
        ))}
      </ScrollView>

      <Portal>
        {/* Snackbar de feedback */}
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar(prev => ({ ...prev, visible: false }))}
          duration={3000}
          style={{
            backgroundColor: 
              snackbar.tipo === 'success' ? '#4CAF50' : 
              snackbar.tipo === 'error' ? '#F44336' :
              '#2196F3'
          }}
        >
          {snackbar.mensagem}
        </Snackbar>

        <Modal
          visible={filterModalVisible}
          onDismiss={() => setFilterModalVisible(false)}
          contentContainerStyle={styles.modalContent}
        >
          <ScrollView style={styles.modalScroll}>
            <Text variant="titleLarge" style={styles.modalTitle}>Filtros</Text>
            
            <Surface style={styles.filterCard}>
              <View style={styles.filterRow}>
                <View style={[styles.filterSection, styles.filterHalf]}>
                  <Text variant="labelSmall" style={styles.sectionTitle}>Filial</Text>
                  <TextInput
                    dense
                    mode="outlined"
                    value={filters.filial}
                    onChangeText={(text) => setFilters({ ...filters, filial: text })}
                    style={styles.filterInput}
                    keyboardType="numeric"
                  />
                </View>
                <View style={[styles.filterSection, styles.filterHalf]}>
                  <Text variant="labelSmall" style={styles.sectionTitle}>N° Carregamento</Text>
                  <TextInput
                    dense
                    mode="outlined"
                    value={filters.numCarregamento}
                    onChangeText={(text) => setFilters({ ...filters, numCarregamento: text })}
                    style={styles.filterInput}
                    keyboardType="numeric"
                  />
                </View>
              </View>
            </Surface>

            <Surface style={styles.filterCard}>
              <Text variant="labelSmall" style={styles.sectionTitle}>Data de Emissão</Text>
              <View style={styles.dateContainer}>
                <DatePickerInput
                  locale="pt"
                  label="De"
                  value={filters.dataEmissaoInicial ? new Date(filters.dataEmissaoInicial) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataEmissaoInicial: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="start"
                />
                <DatePickerInput
                  locale="pt"
                  label="Até"
                  value={filters.dataEmissaoFinal ? new Date(filters.dataEmissaoFinal) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataEmissaoFinal: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="end"
                />
              </View>

              <Text variant="labelSmall" style={[styles.sectionTitle, { marginTop: 8 }]}>Data de Vencimento</Text>
              <View style={styles.dateContainer}>
                <DatePickerInput
                  locale="pt"
                  label="De"
                  value={filters.dataVencimentoInicial ? new Date(filters.dataVencimentoInicial) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataVencimentoInicial: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="start"
                />
                <DatePickerInput
                  locale="pt"
                  label="Até"
                  value={filters.dataVencimentoFinal ? new Date(filters.dataVencimentoFinal) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataVencimentoFinal: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="end"
                />
              </View>
            </Surface>

            <Surface style={styles.filterCard}>
              <Text variant="labelSmall" style={styles.sectionTitle}>Data de Prorrogação</Text>
              <View style={styles.dateContainer}>
                <DatePickerInput
                  locale="pt"
                  label="De"
                  value={filters.dataProrrogacaoInicial ? new Date(filters.dataProrrogacaoInicial) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataProrrogacaoInicial: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="start"
                />
                <DatePickerInput
                  locale="pt"
                  label="Até"
                  value={filters.dataProrrogacaoFinal ? new Date(filters.dataProrrogacaoFinal) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataProrrogacaoFinal: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="end"
                />
              </View>

              <Text variant="labelSmall" style={[styles.sectionTitle, { marginTop: 8 }]}>Data de Desconto</Text>
              <View style={styles.dateContainer}>
                <DatePickerInput
                  locale="pt"
                  label="De"
                  value={filters.dataDescontoInicial ? new Date(filters.dataDescontoInicial) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataDescontoInicial: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="start"
                />
                <DatePickerInput
                  locale="pt"
                  label="Até"
                  value={filters.dataDescontoFinal ? new Date(filters.dataDescontoFinal) : undefined}
                  onChange={(date) => setFilters({ ...filters, dataDescontoFinal: date?.toISOString() || '' })}
                  style={[styles.filterInput, styles.dateInput]}
                  mode="outlined"
                  inputMode="end"
                />
              </View>
            </Surface>

            {savedFilters.length > 0 && (
              <Surface style={styles.filterCard}>
                <Text variant="labelSmall" style={styles.sectionTitle}>Filtros Salvos</Text>
                <View style={styles.filterChipsContainer}>
                  {savedFilters.map((filter) => (
                    <Button
                      key={filter.id}
                      mode="outlined"
                      compact
                      style={styles.filterChip}
                      onPress={() => setFilters(filter.filters)}
                      icon="filter"
                    >
                      {filter.name}
                    </Button>
                  ))}
                </View>
              </Surface>
            )}

            <Surface style={styles.filterCard}>
              <Text variant="labelSmall" style={styles.sectionTitle}>Salvar Filtro</Text>
              <TextInput
                dense
                mode="outlined"
                placeholder="Nome do Filtro"
                value={filterName}
                onChangeText={setFilterName}
                style={styles.filterInput}
              />
              <Button
                mode="outlined"
                onPress={() => {
                  if (filterName.trim()) {
                    const newFilter: SavedFilter = {
                      id: Date.now().toString(),
                      name: filterName.trim(),
                      filters: { ...filters },
                    };
                    setSavedFilters([...savedFilters, newFilter]);
                    setFilterName('');
                  }
                }}
                style={styles.saveButton}
              >
                Salvar Filtro
              </Button>
            </Surface>

            <Button
              mode="contained"
              onPress={aplicarFiltros}
              style={styles.applyButton}
            >
              Aplicar Filtros
            </Button>
          </ScrollView>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    padding: 16,
    elevation: 4,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
  },
  accordionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  },
  clientInfo: {
    flex: 1,
  },
  sendClientButton: {
    marginLeft: 16,
  },
  headerLeft: {
    flex: 1,
  },
  selectAllButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  clientStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  selectedCount: {
    color: '#1976D2',
    fontWeight: 'bold',
  },
  valueCell: {
    paddingHorizontal: 16,
    minWidth: 120,
  },
  dateCell: {
    paddingHorizontal: 16,
    minWidth: 100,
  },
  duplicataHeader: {
    flex: 1,
    paddingHorizontal: 16,
  },
  valueHeader: {
    minWidth: 120,
    paddingHorizontal: 16,
  },
  dateHeader: {
    minWidth: 100,
    paddingHorizontal: 16,
  },
  actionsHeader: {
    minWidth: 100,
    paddingHorizontal: 16,
  },
  clientCheckbox: {
    marginRight: 8,
    justifyContent: 'center',
  },
  boletoCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  selectAllContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  filterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  filterHalf: {
    flex: 1,
    marginHorizontal: 2,
  },
  filterSection: {
    marginBottom: 2,
  },
  sectionTitle: {
    marginBottom: 2,
    fontSize: 12,
    opacity: 0.7,
  },
  filterChip: {
    margin: 2,
    height: 32,
  },
  filterChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  saveButton: {
    marginTop: 4,
    marginTop: 16,
  },
  searchBar: {
    flex: 1,
    marginRight: 8,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: 12,
    margin: 16,
    borderRadius: 8,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalScroll: {
    flex: 1,
  },
  filterCard: {
    marginBottom: 8,
    padding: 8,
    borderRadius: 4,
    elevation: 2,
  },
  modalTitle: {
    marginBottom: 8,
    fontSize: 18,
  },
  filterInput: {
    marginBottom: 8,
    backgroundColor: 'transparent',
    height: 40,
  },
  dateContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: 8,
  },
  dateInput: {
    flex: 1,
    height: 40,
  },
  applyButton: {
    marginTop: 8,
  },
});
